import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Badge, Box, LinearProgress, Paper, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useIsClientOnboarding } from '../../../hooks/client/useIsClientOnboarding';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useClientVertical } from '../../../hooks/display/useClientVertical';
import { useClientParentOrganization } from '../../../hooks/display/useClientParentOrganization';
import { useFeatureClientOnboarding } from '../../../hooks/features/useFeatureClientOnboarding';

import { TransportationBenefitModelState } from '../../../data/models/transportation-benefit-model.model';

import * as lookupSlice from '../../../data/lookup.slice';
import * as clientSlice from '../../../data/client.slice';
import * as accountingSlice from '../../../data/accounting.slice';

import ClientHeader from './ClientHeader';
import ClientDetails from './ClientDetails';
import OnboardingTasks from './OnboardingTasks';

import TextSearch from '../../controls/TextSearch';
import ClientAdministration from './ClientAdministration';

const PREFIX = 'Client';

const classes = {
  container: `${PREFIX}-container`,
  search: `${PREFIX}-search`,
  cards: `${PREFIX}-cards`,
  card: `${PREFIX}-card`,
  cardTitle: `${PREFIX}-cardTitle`,
  badge: `${PREFIX}-badge`,
  cardName: `${PREFIX}-cardName`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    gap: theme.spacing(4),
  },
  [`& .${classes.search}`]: {
    width: '330px',
  },
  [`& .${classes.cards}`]: {
    display: 'flex',
    gap: theme.spacing(3),
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  [`& .${classes.card}`]: {
    minWidth: '200px',
    padding: `${theme.spacing(1)} ${theme.spacing(3)}`,
    color: theme.palette.lightActionDisabled60p.main,
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
    letterSpacing: '0.17px',
    cursor: 'pointer',
  },
  [`& .${classes.cardTitle}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.lightTextPrimary.main,
    fontSize: '16px',
    fontWeight: '600',
    lineHeight: '175%',
    letterSpacing: '0.15px',
  },

  [`& .${classes.badge}`]: {
    display: 'flex',
    height: '20px',
    padding: '0 6.5px',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '64px',
    backgroundColor: theme.palette.lightPrimaryShades08p.main,

    color: theme.palette.buttonColor.main,
    textAlign: 'center',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontFamily: 'Source Sans Pro',
    fontSize: '11px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '157%',
    letterSpacing: '0.1px',
  },
  [`& .${classes.cardName}`]: {
    color: theme.palette.lightTextPrimary.main,
    fontSize: '16px',
    fontWeight: '600',
    lineHeight: '175%',
    letterSpacing: '0.15px',
  },
}));

interface CardProps {
  title: string;
  count?: number;
  subtitle?: string;
  detail1?: string;
  detail2?: string;
  link?: string;
  onClick?: () => void;
}

function Card({
  title,
  count,
  subtitle,
  detail1,
  detail2,
  link,
  onClick,
}: CardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (link) {
      navigate(link);
      const home = document.querySelector('.Home-content');
      home?.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (onClick) {
      onClick();
    }
  };

  return (
    <Paper className={classes.card} onClick={handleClick}>
      <Box className={classes.cardTitle}>
        <Box>{title} </Box>
        {count !== undefined && (
          <Badge className={classes.badge}>{count}</Badge>
        )}
      </Box>
      {subtitle && <Box className={classes.cardName}>{subtitle}</Box>}
      {detail1 && <Box>{detail1}</Box>}
      {detail2 && <Box>{detail2}</Box>}
    </Paper>
  );
}

export default function Client() {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const enableClientOnboarding = useFeatureClientOnboarding();
  const params = useParams<{ id: string }>();
  const client = useAppSelector(clientSlice.selectCurrentClient);
  const clientBC = useRef(client?.clientBC);
  const isSuperReader = useIsSuperReaderUser();
  const [clientAdminDialogOpen, setClientAdminDialogOpen] = useState(false);
  const loadingClient = useAppSelector(clientSlice.selectLoadingCurrentClient);
  const isOnboarding = useIsClientOnboarding(client);
  const programs = useAppSelector(clientSlice.selectPrograms);
  const servicePlans = useAppSelector(clientSlice.selectServicePlanList);
  const purposeCodes = useAppSelector(clientSlice.selectPurposeCodes);
  // const serviceLevels = useAppSelector(clientSlice.selectServiceLevels);
  const serviceLevels: any[] = [];
  const serviceAreas = useAppSelector(clientSlice.selectServiceAreas);
  // const serviceLocations = useAppSelector(clientSlice.selectServiceLocations);
  const serviceLocations: any[] = [];
  const benefits = useAppSelector(clientSlice.selectTransportationBenefits);

  const vertical = useClientVertical(client?.clientVerticalBC);
  const parentOrg = useClientParentOrganization(client?.parentOrganizationBC);

  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    dispatch(lookupSlice.fetchLookupItemsAsync('benefitPurpose'));
    dispatch(lookupSlice.fetchLookupItemsAsync('lineOfBusiness'));
    dispatch(lookupSlice.fetchLookupItemsAsync('chargeType'));
    dispatch(lookupSlice.fetchLookupItemsAsync('pricingCalculationGroup'));
    dispatch(lookupSlice.fetchLookupItemsAsync('purposeCodeModelType'));
    dispatch(
      lookupSlice.fetchLookupItemsAsync('transportationAttributeCategory'),
    );
    dispatch(
      clientSlice.fetchTransportationBenefitModelsAsync([
        TransportationBenefitModelState.active,
      ]),
    );
    dispatch(accountingSlice.fetchTransportationServicesAsync(['active']));
    dispatch(accountingSlice.fetchPricingFunctionsAsync());
    dispatch(accountingSlice.fetchTransportationAttributesAsync());
    dispatch(clientSlice.fetchTransportationBenefitFunctionsAsync());
    dispatch(accountingSlice.fetchPricingModelsAsync());
  }, []);

  useEffect(() => {
    if (client?.clientBC && clientBC.current !== client?.clientBC) {
      clientBC.current = client?.clientBC;
      dispatch(clientSlice.fetchClientAddressesAsync(clientBC.current));
      dispatch(clientSlice.fetchClientContactsAsync(clientBC.current));
      dispatch(clientSlice.fetchClientPurposeCodesAsync(clientBC.current));
      dispatch(clientSlice.fetchClientServiceAreasAsync(clientBC.current));
      dispatch(clientSlice.fetchTransportationBenefitsAsync(clientBC.current));
      dispatch(clientSlice.fetchClientServicePlansAsync(clientBC.current));
      dispatch(clientSlice.fetchClientProgramsAsync(clientBC.current));
      dispatch(clientSlice.fetchClientSettingsAsync(clientBC.current));
    }
  }, [client?.clientBC]);

  const showOperationsSection = useMemo(() => {
    return !!(programs?.length || servicePlans?.servicePlans?.length);
  }, [programs, servicePlans]);

  const showClientOptionsSection = useMemo(() => {
    return !!(
      purposeCodes?.length ||
      serviceLevels?.length ||
      serviceAreas?.length ||
      serviceLocations?.length ||
      benefits?.length
    );
  }, [purposeCodes, serviceLevels, serviceAreas, serviceLocations, benefits]);

  const onSearchTextChange = (val: string) => {
    setSearchText(val);
  };

  const handleClientAdminClose = () => {
    setClientAdminDialogOpen(false);
  };

  return (
    <StyledBox className={classes.container}>
      {loadingClient && <LinearProgress color="primary" />}
      {enableClientOnboarding && !loadingClient && (
        <>
          <ClientHeader
            name={client?.legalName || ''}
            isOnboarding={isOnboarding}
          />
          {!isSuperReader && isOnboarding && clientBC.current && (
            <OnboardingTasks clientBC={clientBC.current} />
          )}
          <TextSearch
            id="text-search"
            className={classes.search}
            icon={<SearchIcon />}
            label={t('client.search')}
            currentSearchFilter={searchText}
            onSearchChange={onSearchTextChange}></TextSearch>
          <Typography variant="h3">{t('client.company')}</Typography>
          <Box className={classes.cards}>
            <Card
              title={t('client.profile')}
              subtitle={client?.legalName}
              detail1={parentOrg}
              detail2={vertical}
            />
            <Card
              title={t('client.administration')}
              onClick={() => setClientAdminDialogOpen(true)}
            />
          </Box>
          {showOperationsSection && (
            <>
              <Typography variant="h3">{t('client.operations')}</Typography>
              <Box className={classes.cards}>
                {!!programs.length && (
                  <Link
                    to={`/clients/${client?.clientBC}/programs`}
                    style={{ textDecoration: 'none' }}>
                    <Card
                      title={t('client.programs')}
                      count={programs.length}
                    />
                  </Link>
                )}
                {!!servicePlans?.servicePlans?.length && (
                  <Link
                    to={`/clients/${client?.clientBC}/servicePlans`}
                    style={{ textDecoration: 'none' }}>
                    <Card
                      title={t('client.servicePlans')}
                      count={servicePlans.servicePlans.length}
                    />
                  </Link>
                )}
              </Box>
            </>
          )}
          {showClientOptionsSection && (
            <>
              <Typography variant="h3">{t('client.clientOptions')}</Typography>
              <Box className={classes.cards}>
                {!!purposeCodes.length && (
                  <Card
                    title={t('client.purposeCodes')}
                    count={purposeCodes.length}
                  />
                )}
                {!!serviceLevels.length && (
                  <Card
                    title={t('client.serviceLevels')}
                    count={serviceLevels.length}
                  />
                )}
                {!!serviceAreas.length && (
                  <Card
                    title={t('client.serviceAreas')}
                    count={serviceAreas.length}
                    link={`/clients/${client?.clientBC}/serviceAreas`}
                  />
                )}
                {!!serviceLocations.length && (
                  <Card
                    title={t('client.locations')}
                    count={serviceLocations.length}
                  />
                )}
                {!!benefits.length && (
                  <Link
                    to={`/clients/${client?.clientBC}/transportation-benefits`}
                    style={{ textDecoration: 'none' }}>
                    <Card
                      title={t('client.transportationBenefits')}
                      count={benefits.length}
                    />
                  </Link>
                )}
              </Box>
            </>
          )}
        </>
      )}

      {!enableClientOnboarding && (
        <>
          <ClientDetails client={client || undefined} readonly={false} />
        </>
      )}
      <ClientAdministration
        clientBC={client?.clientBC}
        open={clientAdminDialogOpen}
        handleClose={handleClientAdminClose}
      />
    </StyledBox>
  );
}
